/**
 * useGlobalObjectPreviewPanels — lightweight hook for opening/closing
 * Jira preview panels from any component.
 *
 * Imports only from JiraPreviewPanelsContext.tsx (no heavy transitive deps).
 * Hook consumers can import from this file instead of PreviewPanelsProvider.tsx
 * to avoid pulling in the @atlassian/preview-panel-global-objects subtree.
 */
import { useCallback, useContext } from 'react';
import { JiraPreviewPanelsContext } from './JiraPreviewPanelsContext.tsx';

const parentProduct: string = 'jira-software';

type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;

type IsPreviewRestricted = (props: { ari: string }) => boolean;

export const useGlobalObjectPreviewPanels = (): {
	openPreviewPanel: (props: { url: string; ari: string; name: string; iconUrl?: string }) => void;
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
	isPreviewRestricted: IsPreviewRestricted;
	closePreviewPanel: () => void;
} => {
	const context = useContext(JiraPreviewPanelsContext);
	if (!context) {
		throw new Error('useGlobalObjectPreviewPanels must be used within a PreviewPanelsProvider');
	}

	const {
		openPreviewPanel: openGlobalPreviewPanel,
		isPreviewPanelAvailable,
		isPreviewRestricted,
	} = context;

	const openPreviewPanel = useCallback(
		(props: { url: string; ari: string; name: string; iconUrl?: string }): void => {
			openGlobalPreviewPanel({ ...props, parentProduct });
		},
		[openGlobalPreviewPanel],
	);

	return {
		openPreviewPanel,
		isPreviewPanelAvailable,
		isPreviewRestricted,
		closePreviewPanel: context.closePreviewPanel,
	};
};
